import React, { useState, useMemo } from 'react';
import {
  DataGrid,
  GridColDef,
  GridFilterInputValue,
  GridFilterItem,
  GridToolbarQuickFilter,
  svSE,
  GridRenderCellParams,
  GridCellParams,
} from '@mui/x-data-grid';
import { styled } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Button,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { RootState } from '../../store/types/sharedTypes';
import { IUser } from '../../lib/interfaces';

import { getFormattedPersonalNumber } from '../../lib/helpers/personalNumber';
import i18n from '../../i18next';
import { GENERAL_FONT, MHColours } from '../../constants/theme';
import { reportError } from '../../lib/sentry';
import { sendAppointmentBookedRequest } from '../../lib/services/generalService';

import type { AppDispatch } from '../../store';
import { getAllPatients } from '../../store/actions/general';
import { formatTimestamp } from '../../utils/dateUtils';
import { isSuperAdmin } from '../../lib/authz';
import {
  isReferralPatient,
  isReadyToBook,
  isRegisteredAwaitingForm,
} from '../../lib/helpers/patientStatus';
import { DS } from '../PatientForms/designTokens';
import FlashSnackbar, { Severity } from '../common/FlashSnackbar';
import { MANAGE_ACTIONS, ManageAction } from './manageActions';
import {
  PriorityLevel,
  PriorityFilter,
  PRIORITY_LEVELS,
  excludeDemoUsers,
  countByPriority,
  filterAndSortUsers,
} from '../../lib/helpers/priorityFilter';

const GridContainer = styled.div`
  height: 100%;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const normalizePersonalNumber = (pnr: string) => {
  if (!pnr) return '';
  return pnr.replace(/-/g, '');
};

const filterPersonalNumber = (filterValue: string, cellValue: string) => {
  if (!filterValue || !cellValue) return false;

  const normalizedFilter = normalizePersonalNumber(filterValue);
  const normalizedCellValue = normalizePersonalNumber(cellValue);

  return normalizedCellValue.includes(normalizedFilter);
};

interface UsersTableProps {
  rowsPerPage?: number;
  admins?: boolean;
}

const PRIORITY_COLORS: Record<
  PriorityLevel,
  { text: string; chipBg: string; chipBorder: string }
> = {
  P1: {
    text: '#C1342B',
    chipBg: 'rgba(193,52,43,0.08)',
    chipBorder: '#C1342B',
  },
  P2: {
    text: '#D9871F',
    chipBg: 'rgba(217,135,31,0.08)',
    chipBorder: '#D9871F',
  },
  P3: { text: '#0E6C68', chipBg: '#CAE9E4', chipBorder: '#0E6C68' },
};

const manageButtonSx = {
  fontFamily: GENERAL_FONT,
  fontWeight: 400,
  backgroundColor: MHColours.mhGreen,
  color: '#FFFFFF',
  borderRadius: '999px',
  textTransform: 'none' as const,
  px: 2,
  py: 0.75,
  minWidth: 0,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: MHColours.mhGreen,
    boxShadow: 'none',
    filter: 'brightness(0.96)',
  },
  '& .MuiButton-endIcon': { marginLeft: '6px' },
  '& .MuiButton-endIcon > svg': { fontSize: 16 },
};

const getStatusDetails = (user: IUser, t: TFunction) => {
  const timestamp = formatTimestamp(user.createdAt || new Date(), {
    useRelativeTimeForMonth: true,
  });
  const signupLabel = user.signupType ? t(`signupType.${user.signupType}`) : '';

  if (isReadyToBook(user)) {
    const createdLabel = isReferralPatient(user)
      ? `${t('label.invited')} ${timestamp}`
      : `${t('label.initiated')} ${timestamp}`;
    return {
      label: t('label.new'),
      color: '#CAE9E4',
      textColor: '#0E6C68',
      tooltip: `${signupLabel} - ${createdLabel}`,
    };
  }

  if (isRegisteredAwaitingForm(user)) {
    return {
      label: t('label.registered'),
      color: '#D8DBFF',
      textColor: '#242829',
      tooltip: `${signupLabel} - ${t('label.initiated')} ${timestamp}`,
    };
  }

  if (isReferralPatient(user) && !user.onboardingCompleted) {
    return {
      label: t('label.invited'),
      color: '#0E6C68',
      textColor: '#F1EDE6',
      tooltip: `${signupLabel} - ${t('label.invited')} ${timestamp}`,
    };
  }

  if (user.signupType === 'selfSignup' && !user.onboardingCompleted) {
    return {
      label: t('label.initiated'),
      color: '#EBB194',
      textColor: '#242829',
      tooltip: `${signupLabel} - ${t('label.initiated')} ${timestamp}`,
    };
  }

  if (user.signupType === 'legacy' && !user.onboardingCompleted) {
    return {
      label: t('label.migrated'),
      color: '#D9871F',
      textColor: '#FFFFFF',
      tooltip: `${signupLabel} - ${t('label.migrated')} ${timestamp}`,
    };
  }

  return null;
};

const getStatusLabel = (user: IUser, t: TFunction) => {
  const status = getStatusDetails(user, t);

  if (status) {
    return (
      <Tooltip title={status.tooltip ?? ''}>
        <Chip
          label={status.label}
          style={{
            backgroundColor: status.color,
            color: status.textColor,
            fontWeight: 400,
            borderRadius: '999px',
          }}
        />
      </Tooltip>
    );
  }

  return null;
};

const baseColumns: GridColDef[] = [
  {
    field: 'personalNumber',
    headerName: i18n.t('personalNumber'),
    width: 200,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params: GridRenderCellParams<IUser>) => (
      <>{getFormattedPersonalNumber(params.row.personalNumber)}</>
    ),
    filterable: true,
    getApplyQuickFilterFn: (filterValue: string) => (params: GridCellParams) =>
      filterPersonalNumber(filterValue, String(params.value || '')),

    filterOperators: [
      {
        label: 'Innehåller',
        value: 'contains',

        getApplyFilterFn: (filterItem: GridFilterItem) => {
          if (!filterItem.value) {
            return null;
          }
          return () => false;
        },
        getApplyFilterFnV7: (filterItem: GridFilterItem) => {
          if (!filterItem.value) {
            return null;
          }
          return (value: string) =>
            filterPersonalNumber(filterItem.value, value);
        },
        InputComponent: GridFilterInputValue,
        InputComponentProps: { placeholder: 'Personnummer...' },
      },
    ],
  },
  {
    field: 'firstName',
    headerName: i18n.t('firstName'),
    flex: 1,
    minWidth: 160,
    align: 'left',
    headerAlign: 'left',
  },
  {
    field: 'lastName',
    headerName: i18n.t('lastName'),
    flex: 1,
    minWidth: 160,
    align: 'left',
    headerAlign: 'left',
  },
];

const UsersTable = (props: UsersTableProps) => {
  const { rowsPerPage, admins } = props;
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuUser, setMenuUser] = useState<IUser | null>(null);
  const [dialogAction, setDialogAction] = useState<ManageAction | null>(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [flash, setFlash] = useState<{
    open: boolean;
    msg: string;
    severity: Severity;
  }>({ open: false, msg: '', severity: 'success' });
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>(null);

  const dispatch = useDispatch<AppDispatch>();
  const {
    sessionUser,
    patients: users,
    loading: isLoading,
  } = useSelector((state: RootState) => state.general);

  const navigate = useNavigate();

  const handleManageMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    user: IUser
  ) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuUser(user);
  };

  const handleManageMenuClose = () => {
    setMenuAnchor(null);
    setMenuUser(null);
  };

  const handleManageActionPick = (action: ManageAction) => {
    setSelectedUser(menuUser);
    setDialogAction(action);
    handleManageMenuClose();
  };

  const handleDialogClose = () => {
    setDialogAction(null);
    setSelectedUser(null);
  };

  const handleDialogConfirm = async () => {
    if (!selectedUser || !dialogAction) return;

    const { notify, toastKey } = MANAGE_ACTIONS[dialogAction];
    setIsSubmitting(true);
    try {
      await sendAppointmentBookedRequest(selectedUser._id, notify);

      const getAdmins = window.location.pathname.includes('admins');
      dispatch(getAllPatients(getAdmins));
      setFlash({ open: true, msg: t(toastKey), severity: 'success' });
    } catch (error) {
      reportError(error);
      setFlash({ open: true, msg: t('manageToast.error'), severity: 'error' });
    } finally {
      setIsSubmitting(false);
      setDialogAction(null);
      setSelectedUser(null);
    }
  };

  // the dialog stays mounted while it closes, so a null action keeps the booked
  // copy rather than blanking the text mid-animation
  const dialogCopy = MANAGE_ACTIONS[dialogAction ?? 'booked'];

  const nonDemoUsers = useMemo(() => excludeDemoUsers(users || []), [users]);

  const priorityCounts = useMemo(
    () => countByPriority(nonDemoUsers),
    [nonDemoUsers]
  );

  const sortedUsers = useMemo(
    () => filterAndSortUsers(nonDemoUsers, priorityFilter),
    [nonDemoUsers, priorityFilter]
  );

  const columns = useMemo(() => {
    const priorityColumn: GridColDef = {
      field: 'priority',
      headerName: i18n.t('priority'),
      width: 90,
      align: 'left',
      headerAlign: 'left',
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<IUser>) => {
        const { priority } = params.row;
        if (!priority) return null;
        const colors = PRIORITY_COLORS[priority];
        return (
          <Chip
            label={priority}
            variant="outlined"
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              borderColor: colors.chipBorder,
              color: colors.text,
              borderRadius: '999px',
              height: 26,
            }}
          />
        );
      },
    };

    const statusAndActionColumns: GridColDef[] = [
      {
        field: 'status',
        headerName: i18n.t('status'),
        width: 150,
        align: 'center',
        headerAlign: 'center',
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams<IUser>) => {
          const user = params.row;
          if (isReadyToBook(user)) return t('label.new');
          if (isRegisteredAwaitingForm(user)) return t('label.registered');
          if (isReferralPatient(user) && !user.onboardingCompleted)
            return t('label.invited');
          if (user.signupType === 'selfSignup' && !user.onboardingCompleted)
            return t('label.initiated');
          if (user.signupType === 'legacy' && !user.onboardingCompleted)
            return t('label.migrated');
          return '';
        },
        renderCell: (params: GridRenderCellParams<IUser>) =>
          getStatusLabel(params.row, t),
      },
      {
        field: 'action',
        headerName: i18n.t('action'),
        width: 220,
        flex: 0,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<IUser>) => {
          if (isReadyToBook(params.row)) {
            return (
              <Button
                variant="contained"
                disableElevation
                endIcon={<KeyboardArrowDownIcon />}
                sx={manageButtonSx}
                onClick={(event) => handleManageMenuOpen(event, params.row)}
              >
                {t('label.manage')}
              </Button>
            );
          }

          return null;
        },
        sortable: false,
        filterable: false,
      },
    ];

    return admins
      ? [
          ...baseColumns,
          {
            field: 'profession',
            headerName: t('profession'),
            sortable: true,
            width: 200,
            renderCell: (params: GridRenderCellParams<IUser>) =>
              `${t(params.row.profession || '')}`,
          },
        ]
      : [priorityColumn, ...baseColumns, ...statusAndActionColumns];
  }, [admins, t]);

  const quickSearchToolbar = () => (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <GridToolbarQuickFilter
        debounceMs={200}
        style={{
          border: '1px solid rgb(224 224 224)',
          borderRadius: '5px',
          padding: '12px',
        }}
      />
      {!admins && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <Chip
            label={`${t('priority.all')} · ${nonDemoUsers.length}`}
            variant={priorityFilter === null ? 'filled' : 'outlined'}
            onClick={() => setPriorityFilter(null)}
            sx={{
              fontWeight: priorityFilter === null ? 700 : 400,
              cursor: 'pointer',
              borderRadius: '999px',
              borderColor: 'rgba(36,40,41,0.24)',
              ...(priorityFilter === null && {
                backgroundColor: '#F1EDE6',
                color: '#242829',
                border: '1.5px solid #0E6C68',
              }),
            }}
          />
          {PRIORITY_LEVELS.map((level) => {
            const colors = PRIORITY_COLORS[level];
            const isActive = priorityFilter === level;
            return (
              <Chip
                key={level}
                label={`${t(`priority.${level}`)} · ${priorityCounts[level]}`}
                variant={isActive ? 'filled' : 'outlined'}
                onClick={() => setPriorityFilter(isActive ? null : level)}
                sx={{
                  fontWeight: isActive ? 700 : 400,
                  cursor: 'pointer',
                  borderRadius: '999px',
                  borderColor: colors.chipBorder,
                  color: colors.text,
                  ...(isActive && {
                    backgroundColor: colors.chipBg,
                    border: `1.5px solid ${colors.chipBorder}`,
                  }),
                }}
              />
            );
          })}
        </Box>
      )}
      {!admins && (
        <Button
          size="small"
          onClick={() => setLegendOpen(true)}
          startIcon={<InfoOutlinedIcon fontSize="small" />}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
            fontWeight: 400,
            marginLeft: 'auto',
            flexShrink: 0,
          }}
        >
          {t('legend.showLegend')}
        </Button>
      )}
    </Box>
  );

  return (
    <GridContainer>
      <DataGrid
        loading={isLoading}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          border: '1px solid rgba(36,40,41,0.12)',
          flex: 1,
          minHeight: 0,
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid rgba(36,40,41,0.12)',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(36,40,41,0.08)',
          },
          '& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type':
            {
              paddingLeft: '24px',
            },
          '& .MuiDataGrid-overlayWrapper': {
            minHeight: 56,
          },
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
        }}
        style={{ cursor: 'pointer', fontFamily: GENERAL_FONT }}
        localeText={svSE.components.MuiDataGrid.defaultProps.localeText}
        getRowId={(row: IUser) => row._id}
        onRowClick={(event) => {
          const user = event.row as IUser;

          if (
            user.onboardingCompleted ||
            user.isAdmin ||
            isSuperAdmin(sessionUser)
          ) {
            navigate(`/dashboard/${user._id}`);
          }
        }}
        rows={sortedUsers}
        columns={columns}
        slots={{
          toolbar: quickSearchToolbar,
        }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: rowsPerPage ?? 8 },
          },
          filter: {
            filterModel: {
              items: [],
            },
          },
        }}
        pageSizeOptions={[8, 12, 16, 20, 100]}
        checkboxSelection={false}
      />

      <Dialog
        open={legendOpen}
        onClose={() => setLegendOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { maxWidth: 500 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, py: 1.5 }}>
          {t('legend.title')}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1.5 }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {t('legend.priority.heading')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: '0.8rem' }}
            >
              {t('legend.priority.intro')}
              <br />
              {t('legend.priority.q2')}
              <br />
              {t('legend.priority.q3')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {(
                [
                  {
                    level: 'P1' as PriorityLevel,
                    rules: [t('legend.priority.p1.rule')],
                  },
                  {
                    level: 'P2' as PriorityLevel,
                    rules: [
                      t('legend.priority.p2.rule1'),
                      t('legend.priority.p2.rule2'),
                    ],
                  },
                  {
                    level: 'P3' as PriorityLevel,
                    rules: [t('legend.priority.p3.rule')],
                  },
                ] as { level: PriorityLevel; rules: string[] }[]
              ).map(({ level, rules }) => (
                <Box
                  key={level}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '56px 1fr',
                    gap: 1.5,
                    alignItems: 'start',
                  }}
                >
                  <Chip
                    label={level}
                    variant="outlined"
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      borderColor: PRIORITY_COLORS[level].chipBorder,
                      color: PRIORITY_COLORS[level].text,
                      borderRadius: '999px',
                      height: 24,
                      justifySelf: 'start',
                    }}
                  />
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}
                  >
                    {rules.map((rule) => (
                      <Typography
                        key={rule}
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.8rem' }}
                      >
                        {rule}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {t('legend.status.heading')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {[
                {
                  label: t('label.new'),
                  color: '#CAE9E4',
                  textColor: '#0E6C68',
                  descKey: 'legend.status.new.description',
                },
                {
                  label: t('label.registered'),
                  color: '#D8DBFF',
                  textColor: '#242829',
                  descKey: 'legend.status.registered.description',
                },
                {
                  label: t('label.invited'),
                  color: '#0E6C68',
                  textColor: '#F1EDE6',
                  descKey: 'legend.status.invited.description',
                },
                {
                  label: t('label.initiated'),
                  color: '#EBB194',
                  textColor: '#242829',
                  descKey: 'legend.status.initiated.description',
                },
                {
                  label: t('label.migrated'),
                  color: '#D9871F',
                  textColor: '#FFFFFF',
                  descKey: 'legend.status.migrated.description',
                },
              ].map(({ label, color, textColor, descKey }) => (
                <Box
                  key={label}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '110px 1fr',
                    gap: 1.5,
                    alignItems: 'start',
                  }}
                >
                  <Chip
                    label={label}
                    size="small"
                    sx={{
                      backgroundColor: color,
                      color: textColor,
                      fontWeight: 400,
                      borderRadius: '999px',
                      height: 24,
                      justifySelf: 'start',
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.8rem' }}
                  >
                    {t(descKey)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {t('legend.action.heading')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '110px 1fr',
                gap: 1.5,
                alignItems: 'start',
              }}
            >
              <Button
                variant="contained"
                size="small"
                disableRipple
                disableElevation
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  ...manageButtonSx,
                  fontSize: '0.75rem',
                  px: 1.75,
                  py: 0.5,
                  justifySelf: 'start',
                  cursor: 'default',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('label.manage')}
              </Button>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.8rem' }}
              >
                {t('legend.action.manage.description')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ textTransform: 'none' }}
            onClick={() => setLegendOpen(false)}
          >
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleManageMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 320,
            borderRadius: DS.radiusLg,
            boxShadow:
              '0 16px 40px rgba(36,40,41,0.10), 0 4px 12px rgba(36,40,41,0.06)',
            p: 1,
            marginTop: '8px',
          },
        }}
        MenuListProps={{
          sx: { p: 0, display: 'flex', flexDirection: 'column', gap: 0.5 },
        }}
      >
        <MenuItem
          onClick={() => handleManageActionPick('booked')}
          sx={{
            borderRadius: '16px',
            p: 1.5,
            gap: 1.5,
            alignItems: 'flex-start',
            whiteSpace: 'normal',
            '&:hover': { backgroundColor: DS.surfaceCanvas },
          }}
        >
          <Box
            sx={{
              flex: 'none',
              width: 36,
              height: 36,
              borderRadius: '12px',
              backgroundColor: DS.coolMint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EventOutlinedIcon sx={{ fontSize: 18, color: DS.deepTeal }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <Typography
              sx={{
                fontSize: '0.9375rem',
                fontWeight: 600,
                color: DS.softBlack,
              }}
            >
              {t('manageMenu.markBooked.title')}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.8125rem',
                lineHeight: 1.35,
                color: DS.inkSecondary,
              }}
            >
              {t('manageMenu.markBooked.description')}
            </Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ mx: 1.5, my: 0.25 }} />
        <MenuItem
          onClick={() => handleManageActionPick('handled')}
          sx={{
            borderRadius: '16px',
            p: 1.5,
            gap: 1.5,
            alignItems: 'flex-start',
            whiteSpace: 'normal',
            '&:hover': { backgroundColor: DS.surfaceCanvas },
          }}
        >
          <Box
            sx={{
              flex: 'none',
              width: 36,
              height: 36,
              borderRadius: '12px',
              backgroundColor: DS.surfaceTint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckIcon sx={{ fontSize: 18, color: DS.inkSecondary }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <Typography
              sx={{
                fontSize: '0.9375rem',
                fontWeight: 600,
                color: DS.softBlack,
              }}
            >
              {t('manageMenu.markHandled.title')}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.8125rem',
                lineHeight: 1.35,
                color: DS.inkSecondary,
              }}
            >
              {t('manageMenu.markHandled.description')}
            </Typography>
          </Box>
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(dialogAction)}
        onClose={handleDialogClose}
        PaperProps={{
          sx: { borderRadius: '30px', width: 520, maxWidth: '100%', p: 1 },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.5rem',
            fontWeight: 600,
          }}
        >
          {t(dialogCopy.titleKey)}
        </DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
        >
          <DialogContentText sx={{ color: DS.softBlack, lineHeight: 1.45 }}>
            {t(dialogCopy.descriptionKey, {
              firstName: selectedUser?.firstName,
              lastName: selectedUser?.lastName,
            })}
          </DialogContentText>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              px: 2,
              py: 1.75,
              borderRadius: '16px',
              backgroundColor: dialogCopy.noticeBackground,
            }}
          >
            <InfoOutlinedIcon
              sx={{ flex: 'none', mt: '1px', fontSize: 18, color: DS.deepTeal }}
            />
            <Typography
              sx={{
                fontSize: '0.875rem',
                lineHeight: 1.4,
                color: DS.softBlack,
              }}
            >
              {t(dialogCopy.noticeKey)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={handleDialogClose}
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 2.5,
              py: 1,
              color: DS.deepTeal,
              '&:hover': { backgroundColor: DS.surfaceCanvas },
            }}
          >
            {t('manageDialog.cancelButton')}
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogConfirm}
            autoFocus
            disabled={isSubmitting}
            sx={{ ...manageButtonSx, px: 3, py: 1 }}
          >
            {isSubmitting ? t('loading') : t(dialogCopy.confirmKey)}
          </Button>
        </DialogActions>
      </Dialog>

      <FlashSnackbar
        open={flash.open}
        msg={flash.msg}
        severity={flash.severity}
        onClose={() => setFlash((f) => ({ ...f, open: false }))}
      />
    </GridContainer>
  );
};

export default UsersTable;
